import { Button } from '../../../components/Button';
import { TextInput } from '../../../components/TextInput';
import type { SampleEvent, SampleGame } from '../../../types/sampleData';
import { newSampleKey } from '../../../utils/sampleDataTemplate';
import { NumberField, SampleFieldset, SampleRow } from './SampleFormControls';
import {
  fieldId,
  removeAt,
  replaceAt,
  type FieldErrorLookup,
  type SampleSectionProps,
} from './templateEditing';

const emptyGame = (dayOffset: number): SampleGame => ({
  key: newSampleKey('game'),
  dayOffset,
  time: '09:00',
  fieldName: '',
  homeTeamName: '',
  awayTeamName: '',
});

interface EventGamesProps {
  event: SampleEvent;
  eventIndex: number;
  onChange: (games: SampleGame[]) => void;
  errorFor: FieldErrorLookup;
}

type GameTextField = 'time' | 'fieldName' | 'homeTeamName' | 'awayTeamName';

const EventGames = ({ event, eventIndex, onChange, errorFor }: EventGamesProps) => {
  const headingId = `${fieldId(`events[${eventIndex}].games`)}-heading`;
  return (
    <div className="sample-form__nested" role="group" aria-labelledby={headingId}>
      <span className="sample-form__nested-title" id={headingId}>
        Games for event {eventIndex + 1}
      </span>
      {event.games.map((game, index) => {
        const path = (field: string) => `events[${eventIndex}].games[${index}].${field}`;
        const patch = (next: Partial<SampleGame>) => onChange(replaceAt(event.games, index, next));
        const text = (field: GameTextField, label: string, type: 'text' | 'time' = 'text') => (
          <TextInput
            id={fieldId(path(field))}
            className="sample-form__field"
            label={label}
            type={type}
            value={game[field]}
            error={errorFor(path(field))}
            data-testid={`sample-data-game-${field}-${eventIndex}-${index}`}
            onChange={(e) => patch({ [field]: e.target.value } as Partial<SampleGame>)}
          />
        );
        return (
          <SampleRow
            key={game.key}
            label={`Game ${index + 1}`}
            removeLabel={`Remove game ${index + 1} from event ${eventIndex + 1}`}
            removeTestId={`sample-data-remove-game-${eventIndex}-${index}`}
            onRemove={() => onChange(removeAt(event.games, index))}
          >
            <NumberField
              label="Day offset"
              path={path('dayOffset')}
              error={errorFor(path('dayOffset'))}
              testId={`sample-data-game-dayOffset-${eventIndex}-${index}`}
              value={game.dayOffset}
              min={-365}
              max={365}
              onChange={(dayOffset) => patch({ dayOffset: dayOffset ?? 0 })}
            />
            {text('time', 'Time', 'time')}
            {text('fieldName', 'Field')}
            {text('homeTeamName', 'Home team')}
            {text('awayTeamName', 'Away team')}
          </SampleRow>
        );
      })}
      <Button
        type="button"
        variant="secondary"
        className="sample-form__add"
        data-testid={`sample-data-add-game-${eventIndex}`}
        onClick={() => onChange([...event.games, emptyGame(event.startDayOffset)])}
      >
        Add game to event {eventIndex + 1}
      </Button>
    </div>
  );
};

export const EventsFieldset = ({ value, onChange, errorFor }: SampleSectionProps) => {
  const update = (index: number, patch: Partial<SampleEvent>) =>
    onChange({ ...value, events: replaceAt(value.events, index, patch) });
  const newEvent = (): SampleEvent => ({
    key: newSampleKey('event'),
    title: '',
    startDayOffset: 7,
    endDayOffset: 9,
    games: [],
  });

  return (
    <SampleFieldset
      legend="Events"
      hint={<p className="sample-form__hint">Day offsets count from the day the data is seeded.</p>}
      addLabel="Add event"
      addTestId="sample-data-add-event"
      onAdd={() => onChange({ ...value, events: [...value.events, newEvent()] })}
    >
      {value.events.map((event, index) => {
        const path = (field: string) => `events[${index}].${field}`;
        return (
          <SampleRow
            key={event.key}
            label={`Event ${index + 1}`}
            removeLabel={`Remove event ${event.title || index + 1}`}
            removeTestId={`sample-data-remove-event-${index}`}
            onRemove={() => onChange({ ...value, events: removeAt(value.events, index) })}
          >
            <TextInput
              id={fieldId(path('title'))}
              className="sample-form__field"
              label="Title"
              value={event.title}
              error={errorFor(path('title'))}
              data-testid={`sample-data-event-title-${index}`}
              onChange={(e) => update(index, { title: e.target.value })}
            />
            <NumberField
              label="Start day"
              path={path('startDayOffset')}
              error={errorFor(path('startDayOffset'))}
              testId={`sample-data-event-startDayOffset-${index}`}
              value={event.startDayOffset}
              min={-365}
              max={365}
              onChange={(startDayOffset) => update(index, { startDayOffset: startDayOffset ?? 0 })}
            />
            <NumberField
              label="End day"
              path={path('endDayOffset')}
              error={errorFor(path('endDayOffset'))}
              testId={`sample-data-event-endDayOffset-${index}`}
              value={event.endDayOffset}
              min={-365}
              max={365}
              onChange={(endDayOffset) => update(index, { endDayOffset: endDayOffset ?? 0 })}
            />
            <EventGames
              event={event}
              eventIndex={index}
              errorFor={errorFor}
              onChange={(games) => update(index, { games })}
            />
          </SampleRow>
        );
      })}
    </SampleFieldset>
  );
};
